// Emote display system - floating text above wizards and emote UI buttons

import * as THREE from 'three';
import { EmoteType, PlayerRole } from '../multiplayer/protocol';

export interface EmoteConfig {
  type: EmoteType;
  label: string;
  icon: string;
}

export const EMOTE_CONFIGS: EmoteConfig[] = [
  { type: 'well_played', label: 'Well Played', icon: '\uD83D\uDC4F' },
  { type: 'nice_combo', label: 'Nice Combo', icon: '\uD83D\uDD25' },
  { type: 'gg', label: 'GG', icon: '\uD83E\uDD1D' },
  { type: 'wow_emote', label: 'Wow', icon: '\uD83D\uDE2E' },
];

export const EMOTE_LABELS: Record<EmoteType, string> = {
  well_played: 'Well Played!',
  nice_combo: 'Nice Combo!',
  gg: 'GG!',
  wow_emote: 'Wow!',
};

// TTS text for emotes
export const EMOTE_TTS_TEXT: Record<EmoteType, string> = {
  well_played: 'Well Played',
  nice_combo: 'Nice Combo',
  gg: 'Good Game',
  wow_emote: 'Wow',
};

interface ActiveEmote {
  sprite: THREE.Sprite;
  startTime: number;
  duration: number;
  baseY: number;
}

export class EmoteDisplay {
  private scene: THREE.Scene;
  private activeEmotes: ActiveEmote[] = [];

  // Positions corresponding to player1/player2
  private playerPositions: Record<string, THREE.Vector3> = {
    player1: new THREE.Vector3(0, 1, 8),
    player2: new THREE.Vector3(0, 1, -8),
  };

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  setPlayerPosition(role: PlayerRole, position: THREE.Vector3): void {
    if (role === 'player1' || role === 'player2') {
      this.playerPositions[role] = position.clone();
    }
  }

  showEmote(emoteType: EmoteType, player: PlayerRole): void {
    if (player === 'spectator') return;

    const text = EMOTE_LABELS[emoteType];
    if (!text) return;

    const sprite = this.createEmoteSprite(text);
    const pos = this.playerPositions[player];
    const baseY = 5.5;
    sprite.position.set(pos.x, baseY, pos.z);
    this.scene.add(sprite);

    this.activeEmotes.push({
      sprite,
      startTime: Date.now(),
      duration: 2000,
      baseY,
    });
  }

  update(): void {
    const now = Date.now();
    const toRemove: number[] = [];

    for (let i = 0; i < this.activeEmotes.length; i++) {
      const emote = this.activeEmotes[i];
      const elapsed = now - emote.startTime;
      const progress = elapsed / emote.duration;

      if (progress >= 1) {
        toRemove.push(i);
        continue;
      }

      // Float upward
      emote.sprite.position.y = emote.baseY + progress * 1.5;

      // Fade out in last 30%
      if (progress > 0.7) {
        const fadeProgress = (progress - 0.7) / 0.3;
        emote.sprite.material.opacity = 1 - fadeProgress;
      }
    }

    // Remove expired emotes (reverse order to preserve indices)
    for (let i = toRemove.length - 1; i >= 0; i--) {
      const idx = toRemove[i];
      const emote = this.activeEmotes[idx];
      this.scene.remove(emote.sprite);
      emote.sprite.material.map?.dispose();
      emote.sprite.material.dispose();
      this.activeEmotes.splice(idx, 1);
    }
  }

  private createEmoteSprite(text: string): THREE.Sprite {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d')!;

    canvas.width = 512;
    canvas.height = 128;

    // Background pill
    context.fillStyle = 'rgba(0, 0, 0, 0.6)';
    const metrics = context.measureText(text);
    const textWidth = Math.min(metrics.width + 40, 480);
    const pillX = (512 - textWidth) / 2;
    this.roundRect(context, pillX, 20, textWidth, 88, 20);
    context.fill();

    // Text
    context.font = 'Bold 42px Cinzel, serif';
    context.fillStyle = '#FFD700';
    context.textAlign = 'center';
    context.textBaseline = 'middle';

    // Text shadow
    context.shadowColor = 'rgba(0, 0, 0, 0.8)';
    context.shadowBlur = 6;
    context.shadowOffsetX = 2;
    context.shadowOffsetY = 2;

    context.fillText(text, 256, 64);

    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(4, 1, 1);
    sprite.renderOrder = 999;

    return sprite;
  }

  private roundRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
  ): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  dispose(): void {
    for (const emote of this.activeEmotes) {
      this.scene.remove(emote.sprite);
      emote.sprite.material.map?.dispose();
      emote.sprite.material.dispose();
    }
    this.activeEmotes = [];
  }
}
